/**
 * 🪵 **Driftwood on the shore — what the block IS, shared by both sides.**
 *
 * **D105.** 🎯 **haugur, 2026-09-02:** *"accept the poor coast, slow trickle is
 * fine."*
 *
 * ## 🔴 The shore is a POOR one and that is the design, not a compromise
 *
 * The great driftwood shores face the Arctic drift on **Strandir and the north,
 * 250–350 km away**. The farmers of Óttarsstaðir and Lónakot said of our own
 * ground in **1703**: *"lítil rekavon"* — little expectation of driftwood.
 * Brief §3's own words are **"wood arrives on the tide"**, and a trickle is that
 * sentence made true; an abundant shore would quietly delete it.
 *
 * ✅ **So `worldgenVersion` never moved.** The coast stays where it is and the
 * server puts driftwood on it as an ordinary block edit, exactly the way a
 * player's own building is stored. **Nothing anybody has built was
 * invalidated** — which is what D105 bought.
 *
 * ## Why this module is in `shared`
 *
 * The client draws driftwood and the server places it, and **both have to agree
 * about which block id means driftwood and how many may lie on the shore at
 * once.** Two copies of that would eventually disagree.
 */

import { blockIdForKey } from './blocks.ts';

/** The block key. ⚠️ ASCII-folded like every other key — `moberg`, `gragryti`. */
export const DRIFTWOOD_BLOCK = 'rekavidur';

/** The item key. Picking a heap up gives one of these. */
export const DRIFTWOOD_ITEM = 'rekavidur';

/** Is this block a heap of driftwood? */
export function isDriftwood(id: number): boolean {
  const base = blockIdForKey(DRIFTWOOD_BLOCK);
  return base !== undefined && id === base;
}

/**
 * 🔴 **How many heaps may lie on the whole shore at once.**
 *
 * 🎯 **haugur, 2026-09-02, asked directly:** *"six logs is fine for now, we can
 * tweak it later."*
 *
 * ⚠️ **THE CAP IS THE ECONOMY, NOT THE RATE, AND THAT IS COUNTER-INTUITIVE
 * ENOUGH TO WRITE DOWN.** One game day is **20 real minutes**, so a real day
 * away is **72 game days** — any arrival rate slow enough to feel like a trickle
 * during play would still bury the beach overnight. **What actually decides how
 * much wood you get is how many may lie there at once**; the rate only decides
 * how fast the shore refills after you have stripped it.
 *
 * ⚠️ **A PLAYTEST NUMBER**, and D105 says so in as many words: *"it is the
 * number most likely to need changing after the first playtest, and if the wait
 * is tedious rather than tense the fix is the SPAWN RATE, not the coast."*
 */
export const DRIFTWOOD_CAP_DESIGN = 6;

/**
 * 🔴🔴 **A TESTING OVERRIDE. NOT THE DESIGN. IT MUST COME OUT.** 🔴🔴
 *
 * 🎯 **haugur, 2026-09-02:** *"also can we make a little less scarce, at least
 * while we are testing."*
 *
 * ✅ **To put the shore back: set this to `null`.** One value, nothing else —
 * {@link DRIFTWOOD_CAP_DESIGN} above it was never edited, so **D105 and the
 * Project Brief never stopped being true.** Same shape as the tool-speed
 * override in `tuning.json`, and for the same reason: overwriting a number
 * haugur chose would make every document quoting it a lie.
 *
 * ⚠️ **What it changes:** twenty heaps instead of six, and a heap every three
 * game hours instead of eighteen — **about two and a half real minutes instead
 * of fifteen.** A stripped beach refills in under an hour of real time rather
 * than five.
 *
 * 🔴 **THE SCARCITY IS THE DESIGN, AND THIS DELETES IT WHILE IT IS ON.** D105 is
 * explicit: *"the scarcity is the feature, not a defect to compensate for"*, and
 * *"an abundant shore would quietly delete"* brief §3's own sentence, **"wood
 * arrives on the tide"**. This is a testing aid so haugur does not spend an
 * afternoon waiting for a beach, and it is nothing else.
 */
export const DRIFTWOOD_TEST_ABUNDANCE: {
  readonly cap: number;
  readonly calmGameHours: number;
} | null = { cap: 20, calmGameHours: 3 };

/**
 * How many heaps may lie on the shore at once, **as the game is running now**.
 *
 * ⚠️ Ask this, never {@link DRIFTWOOD_CAP_DESIGN} — the override has to reach
 * everything, or half the system runs on one number and half on the other.
 */
export const DRIFTWOOD_CAP = DRIFTWOOD_TEST_ABUNDANCE?.cap ?? DRIFTWOOD_CAP_DESIGN;

/**
 * How long, in game hours, between arrivals when the weather is calm.
 *
 * ⚠️ **Only reached while the shore is under {@link DRIFTWOOD_CAP}** — a full
 * shore is not counting down to anything.
 */
export const DRIFTWOOD_CALM_GAME_HOURS_DESIGN = 18;

/**
 * How long between arrivals in calm weather, **as the game is running now**.
 *
 * ⚠️ Moved by {@link DRIFTWOOD_TEST_ABUNDANCE} while that is on. See its note
 * for what it costs and how to put it back.
 */
export const DRIFTWOOD_CALM_GAME_HOURS =
  DRIFTWOOD_TEST_ABUNDANCE?.calmGameHours ?? DRIFTWOOD_CALM_GAME_HOURS_DESIGN;

/**
 * 🌬️ **How much a storm hurries the tide along.**
 *
 * 🎯 **haugur, 2026-09-02:** *"storms speed it up."*
 *
 * 🔴 **Speeds up, never gates**, and he chose it that way: a quiet month must
 * still bring wood, or the one material you cannot do without would depend on
 * weather nobody controls. **The wait is shortened, never made infinite.**
 */
export const DRIFTWOOD_STORM_FACTOR = 4;

/** Wind, in m/s, at which the tide is running at full {@link DRIFTWOOD_STORM_FACTOR}. */
export const DRIFTWOOD_STORM_WIND = 20;

/**
 * How much faster driftwood arrives at this wind speed.
 *
 * ⚠️ **A ramp rather than a threshold.** A cliff at some wind speed would mean
 * two nights that felt identical produced completely different mornings, and
 * nobody could see why.
 */
export function driftSpeedForWind(windMetresPerSecond: number): number {
  const t = Math.min(1, Math.max(0, windMetresPerSecond / DRIFTWOOD_STORM_WIND));
  return 1 + (DRIFTWOOD_STORM_FACTOR - 1) * t;
}

/* ── 🪨 the kljásteinn — the same shore, the same tide ─────────────────────── */

/**
 * 🪨 **A holed beach stone, the loom's weight — D136, 2026-10-05.** 🎯 haugur:
 * *"Pick holed stones on the shore."* It lies on the same sand and shingle as
 * driftwood, arrives the same way and is picked up the same way; only the
 * numbers are its own. 📖 Real kljásteinar were basalt beach stones, often
 * naturally holed, 0.4–1.1 kg (Hólar, Fjörður; Viðarsdóttir 2025).
 */
export const KLJASTEINN_BLOCK = 'kljasteinn';
export const KLJASTEINN_ITEM = 'kljasteinn';

/** Is this block a kljásteinn lying on the shore? */
export function isKljasteinn(id: number): boolean {
  const base = blockIdForKey(KLJASTEINN_BLOCK);
  return base !== undefined && id === base;
}

/**
 * 🪨 **Up to twelve on the whole shore at once** — one loom's worth. 🎯 His
 * number (D136). ⚠️ **No testing override, on purpose**: three are parked
 * already, and `T` gives an admin stones without waiting for the tide.
 */
export const KLJASTEINN_CAP = 12;

/**
 * ⏱️ **One about every 15 real minutes in calm weather** — 18 game hours, the
 * driftwood's own designed rate. 🎯 His number. Storms hurry it by
 * {@link driftSpeedForWind}, the same ramp — *"faster in storms"*.
 */
export const KLJASTEINN_CALM_GAME_HOURS = 18;
